import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, RotateCcw, Check } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { PRODUCTS, MODULE_PRICES, BASE_PLATFORM_PRICE, NEED_TO_MODULES, rm } from "@/data/content";
import { ProductIcon } from "@/lib/icons";

const SolutionBuilder = () => {
  const { t, pick, lang } = useLanguage();
  const [step, setStep] = useState(0);
  const [industry, setIndustry] = useState(null);
  const [size, setSize] = useState(null);
  const [needs, setNeeds] = useState([]);

  const industries = t("builder.industries");
  const sizes = t("builder.sizes");
  const needsList = t("builder.needs");
  const needsEn = ["HR", "Payroll", "Sales", "CRM", "POS", "Advertising", "AI Chat", "Travel", "Cooperative Management"];

  const toggleNeed = (n) => setNeeds((s) => (s.includes(n) ? s.filter((x) => x !== n) : [...s, n]));

  const modules = useMemo(() => {
    const set = new Set();
    needs.forEach((n, ) => {
      const idx = (lang === "bm" ? t("builder.needs") : needsEn).indexOf(n);
      const key = needsEn[idx >= 0 ? idx : needsEn.indexOf(n)] || n;
      (NEED_TO_MODULES[key] || []).forEach((m) => set.add(m));
    });
    return [...set];
  }, [needs, lang, t]);

  const recommended = useMemo(() => {
    const map = { hr: "hr", payroll: "hr", travel: "travel", management: "management", "ads-booster": "ads-booster", "ai-chat": "ai-chat", store: "store", koperasi: "koperasi" };
    const slugs = [...new Set(modules.map((m) => map[m]).filter(Boolean))];
    return PRODUCTS.filter((p) => slugs.includes(p.slug));
  }, [modules]);

  const monthly = useMemo(
    () => BASE_PLATFORM_PRICE + MODULE_PRICES.filter((m) => modules.includes(m.id)).reduce((a, m) => a + m.price, 0),
    [modules]
  );

  const reset = () => { setStep(0); setIndustry(null); setSize(null); setNeeds([]); };

  const OptionGrid = ({ options, selected, onSelect, multi, prefix }) => (
    <div className="flex flex-wrap gap-3 justify-center">
      {options.map((opt, i) => {
        const on = multi ? selected.includes(opt) : selected === opt;
        return (
          <button
            key={opt}
            data-testid={`${prefix}-option-${i}`}
            onClick={() => (multi ? onSelect(opt) : onSelect(opt))}
            className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
              on ? "border-indigo-500 bg-indigo-500/15 text-white" : "border-slate-700 bg-slate-900/50 text-slate-300 hover:border-slate-500"
            }`}
          >
            {on && <Check className="w-3.5 h-3.5 text-indigo-400" />}
            {opt}
          </button>
        );
      })}
    </div>
  );

  return (
    <div data-testid="solution-builder" className="glass rounded-3xl p-6 sm:p-10 max-w-4xl mx-auto">
      <div className="flex items-center justify-center gap-2 mb-10">
        {[0, 1, 2, 3].map((s) => (
          <span key={s} className={`h-1.5 rounded-full transition-all duration-300 ${s === step ? "w-10 bg-indigo-500" : s < step ? "w-6 bg-indigo-500/50" : "w-6 bg-slate-700"}`} />
        ))}
      </div>

      {step === 0 && (
        <div className="text-center animate-fade-up">
          <h3 className="text-xl sm:text-2xl font-semibold font-heading text-white mb-8">{t("builder.q1")}</h3>
          <OptionGrid options={industries} selected={industry} onSelect={setIndustry} prefix="q1" />
          <StepNav nextDisabled={!industry} onNext={() => setStep(1)} t={t} />
        </div>
      )}

      {step === 1 && (
        <div className="text-center animate-fade-up">
          <h3 className="text-xl sm:text-2xl font-semibold font-heading text-white mb-8">{t("builder.q2")}</h3>
          <OptionGrid options={sizes} selected={size} onSelect={setSize} prefix="q2" />
          <StepNav nextDisabled={!size} onNext={() => setStep(2)} onBack={() => setStep(0)} t={t} />
        </div>
      )}

      {step === 2 && (
        <div className="text-center animate-fade-up">
          <h3 className="text-xl sm:text-2xl font-semibold font-heading text-white mb-8">{t("builder.q3")}</h3>
          <OptionGrid options={needsList} selected={needs} onSelect={toggleNeed} multi prefix="q3" />
          <StepNav nextDisabled={needs.length === 0} nextLabel={t("builder.seeResult")} onNext={() => setStep(3)} onBack={() => setStep(1)} t={t} />
        </div>
      )}

      {step === 3 && (
        <div className="text-center animate-fade-up">
          <p className="text-xs font-mono2 uppercase tracking-[0.25em] text-cyan-400 mb-6">{t("builder.result")}</p>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {recommended.length === 0 && <p className="text-slate-400 text-sm">—</p>}
            {recommended.map((p) => (
              <Link
                key={p.slug}
                to={`/products/${p.slug}`}
                data-testid={`result-product-${p.slug}`}
                className={`flex items-center gap-2.5 card-surface card-surface-hover rounded-2xl px-5 py-3.5 border ${p.accent.border}`}
              >
                <span className={p.accent.text}><ProductIcon name={p.icon} className="w-5 h-5" /></span>
                <span className="text-sm font-semibold text-white">{p.name}</span>
              </Link>
            ))}
          </div>
          <p className="text-sm text-slate-400 mb-1">{t("common.estimated")}</p>
          <p data-testid="result-price" className="text-4xl font-extrabold font-heading text-white mb-8">{rm(monthly)}<span className="text-base font-medium text-slate-500">{t("common.perMonth")}</span></p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact" data-testid="result-cta" className="inline-flex items-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-7 py-3.5 text-sm transition-colors shadow-lg shadow-indigo-500/30">
              {t("common.startWithPlan")} <ArrowRight className="w-4 h-4" />
            </Link>
            <button data-testid="result-restart" onClick={reset} className="inline-flex items-center gap-2 rounded-full border border-slate-700 hover:border-slate-500 text-slate-300 font-medium px-6 py-3.5 text-sm transition-colors">
              <RotateCcw className="w-4 h-4" /> {t("builder.restart")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const StepNav = ({ onNext, onBack, nextDisabled, nextLabel, t }) => (
  <div className="flex items-center justify-center gap-3 mt-10">
    {onBack && (
      <button data-testid="builder-back" onClick={onBack} className="inline-flex items-center gap-2 rounded-full border border-slate-700 text-slate-300 px-5 py-2.5 text-sm font-medium hover:border-slate-500 transition-colors">
        <ArrowLeft className="w-4 h-4" /> {t("builder.back")}
      </button>
    )}
    <button
      data-testid="builder-next"
      onClick={onNext}
      disabled={nextDisabled}
      className="inline-flex items-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed text-white px-6 py-2.5 text-sm font-semibold transition-colors"
    >
      {nextLabel || t("builder.next")} <ArrowRight className="w-4 h-4" />
    </button>
  </div>
);

export default SolutionBuilder;
